'use client';

import { useId, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileDropZone,
  useFileDropZoneState,
  FILE_DROP_ZONE_ACCEPT
} from '@/components/ui/file-drop-zone';
import type { AddDocumentUploadContext, AddDocumentUploadMeta } from './types';

/**
 * The Upload source of {@link AddDocument}: a compact dialog wrapping the
 * standard {@link FileDropZone} (drop or browse), driven by
 * `useFileDropZoneState` so each file gets real progress, validation and abort
 * from the shared transport. Each file that lands fires `onAttached` with its
 * resulting Vale fileId (per-file, so a surface can append as they arrive).
 */
export function AddDocumentUploadDialog({
  open,
  onOpenChange,
  multiple,
  allowCertifiedCopy,
  upload,
  onAttached
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  multiple?: boolean;
  allowCertifiedCopy?: boolean;
  upload: (
    files: File[],
    ctx: AddDocumentUploadContext,
    meta?: AddDocumentUploadMeta
  ) => Promise<number[]>;
  onAttached?: (fileIds: number[]) => void | Promise<void>;
}) {
  // Dialog-level certified-copy toggle (LF-6189): a single flag applied to every
  // file uploaded this session — read live inside the per-file upload callback.
  const [isCertifiedCopy, setIsCertifiedCopy] = useState(false);
  const certifiedId = useId();

  const state = useFileDropZoneState({
    accept: FILE_DROP_ZONE_ACCEPT.documents.accept,
    maxSizeBytes: FILE_DROP_ZONE_ACCEPT.documents.maxSizeBytes,
    multiple: multiple ?? false,
    upload: async (file, ctx) => {
      const ids = await upload(
        [file],
        { onProgress: ctx.onProgress, signal: ctx.signal },
        allowCertifiedCopy ? { isCertifiedCopy } : undefined
      );
      if (ids.length > 0) await onAttached?.(ids);
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          state.clear();
          setIsCertifiedCopy(false);
        }
      }}
    >
      <DialogContent className="max-w-md gap-3 p-4">
        <DialogHeader className="space-y-0">
          <DialogTitle className="text-sm font-semibold">
            Upload {multiple ? 'documents' : 'a document'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Drag files here or browse to upload from your computer.
          </DialogDescription>
        </DialogHeader>

        {/*
          `min-w-0`: DialogContent is a CSS grid, and a grid item defaults to
          `min-width: auto` — so without this a long, unbroken filename below
          blows this item out to its min-content width, taking the column (and
          therefore the Done button beneath it) far past the modal card and off
          screen. `FileRow` already truncates; the constraint chain breaks here,
          above it. Same footgun the choose-existing picker fixes (LF-4329).
        */}
        <FileDropZone
          variant="compact"
          className="min-w-0"
          {...FILE_DROP_ZONE_ACCEPT.documents}
          multiple={multiple ?? false}
          files={state.files}
          onFilesAdded={state.onFilesAdded}
          onRemove={state.onRemove}
          onRetry={state.onRetry}
        />

        {allowCertifiedCopy && (
          <label
            htmlFor={certifiedId}
            className="text-12px text-foreground-secondary flex cursor-pointer items-center gap-2"
          >
            <Checkbox
              id={certifiedId}
              checked={isCertifiedCopy}
              onCheckedChange={(checked) =>
                setIsCertifiedCopy(checked === true)
              }
              className="h-3.5 w-3.5"
            />
            Mark uploads as certified copies
          </label>
        )}

        <div className="flex justify-end pt-1">
          <Button
            density="micro"
            pad="md"
            type="button"
            size="2xs"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
